import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { Input } from '../../primitives/input';
import { cn } from '../../utils/cn';

function getSearchQueryParamKey(queryParam?: string) {
  if (queryParam?.trim()) {
    return queryParam;
  }

  return null;
}

interface InputSearchProps extends Omit<
  React.ComponentProps<typeof Input>,
  'defaultValue' | 'onChange' | 'value'
> {
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (value: string) => void;
  queryParam?: string;
  value?: string;
}

const InputSearch = forwardRef<HTMLInputElement, InputSearchProps>(
  ({ defaultValue = '', onChange, onValueChange, queryParam, value: tmpValue, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQueryParamKey = getSearchQueryParamKey(queryParam);
    const [internalValue, setInternalValue] = useState(() =>
      searchQueryParamKey ? (searchParams.get(searchQueryParamKey) ?? '') : defaultValue,
    );

    const value = tmpValue === undefined ? internalValue : tmpValue;
    const showClearButton = !props.disabled && Boolean(value);
    const clearButtonSizeClass = props.sizesx === 'sm' ? 'size-6' : 'size-7';

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);

    useEffect(() => {
      onValueChange?.(value);
    }, [onValueChange, value]);

    useEffect(() => {
      if (!searchQueryParamKey || tmpValue !== undefined) {
        return;
      }

      const queryValue = searchParams.get(searchQueryParamKey) ?? '';
      setInternalValue((current) => (current === queryValue ? current : queryValue));
    }, [searchParams, searchQueryParamKey, tmpValue]);

    useEffect(() => {
      if (!searchQueryParamKey) {
        return;
      }

      const currentQueryValue = searchParams.get(searchQueryParamKey) ?? '';
      if (currentQueryValue === value) {
        return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      if (value.trim()) {
        nextSearchParams.set(searchQueryParamKey, value);
      } else {
        nextSearchParams.delete(searchQueryParamKey);
      }

      setSearchParams(nextSearchParams, { replace: true });
    }, [value, searchParams, searchQueryParamKey, setSearchParams]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      if (tmpValue === undefined) {
        setInternalValue(event.target.value);
      }

      onChange?.(event);
    };

    const handleClear = () => {
      const inputElement = inputRef.current;
      if (!inputElement) {
        return;
      }

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set;

      nativeInputValueSetter?.call(inputElement, '');
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      inputElement.focus();
    };

    return (
      <div className={cn('relative', props.className)}>
        <MagnifyingGlassIcon className="absolute top-1/2 left-[0.65rem] size-3.5 -translate-y-1/2 p-0" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          {...props}
          className={cn('pb-1 pl-7', { 'pr-9': showClearButton }, props.className)}
        />
        <button
          aria-label="Limpiar búsqueda"
          className={cn(
            'absolute top-1/2 right-1 flex -translate-y-1/2 items-center justify-center rounded-full bg-secondary p-0 text-secondary-foreground shadow-xs transition-[color,background-color,border-color,transform,translate,scale,rotate] duration-200 ease-in-out hover:bg-secondary/70 active:translate-y-[calc(-50%+1px)]',
            clearButtonSizeClass,
            !showClearButton && 'hidden',
          )}
          tabIndex={showClearButton ? 0 : -1}
          type="button"
          onClick={handleClear}
        >
          <Cross2Icon className="size-4" />
        </button>
      </div>
    );
  },
);

InputSearch.displayName = 'InputSearch';

export { InputSearch };
